/**
 * Customer projection — delegates to shared @burnchart/financial core.
 */
import type {
  CustomerProjection,
  CustomerGrowthScenario,
  AcquisitionCostModel,
  SubscriptionTier,
  CustomerCapacityGroup,
  FinancialEvent,
} from '../../types/startup.types';
import {
  calculateMonthlyCustomerMetrics as coreCalculateMonthlyCustomerMetrics,
  calculateEquilibriumCustomers,
  toStandardizedCustomerData,
  type MonthlyCustomerMetrics,
  type CustomerProjectionDataInput,
  type CapacityEventInput,
} from '@burnchart/financial';

export type { MonthlyCustomerMetrics };
export { toStandardizedCustomerData, calculateEquilibriumCustomers };

export interface CustomerProjectionData {
  projections: CustomerProjection[];
  subscriptionTiers: SubscriptionTier[];
  growthScenarios: CustomerGrowthScenario[];
  acquisitionCostModels: AcquisitionCostModel[];
  capacityGroups?: CustomerCapacityGroup[];
  capacityEvents?: CapacityEventInput[];
}

export function buildCapacityEventsFromTimelineEvents(
  events: FinancialEvent[],
): CapacityEventInput[] {
  return events
    .filter(
      (event) =>
        (event.capacityIncrease ?? 0) > 0 &&
        (event.capacityGroupId || event.projectionId),
    )
    .map((event) => ({
      date: event.date,
      capacityGroupId: event.capacityGroupId,
      projectionId: event.projectionId,
      capacityIncrease: event.capacityIncrease,
    }));
}

export function calculateMonthlyCustomerMetrics(
  targetDate: Date,
  data: CustomerProjectionData,
): MonthlyCustomerMetrics {
  return coreCalculateMonthlyCustomerMetrics(targetDate, {
    projections: data.projections as CustomerProjectionDataInput['projections'],
    subscriptionTiers: data.subscriptionTiers,
    acquisitionCostModels: data.acquisitionCostModels,
    growthScenarios: data.growthScenarios,
    capacityGroups: data.capacityGroups,
    capacityEvents: data.capacityEvents,
  });
}

function formatRevenuePlanLabel(tier: SubscriptionTier): string {
  const model = tier.revenueModel ?? 'subscription';
  if (model === 'per_visit') {
    return `${tier.name} - $${tier.pricePerVisit ?? 0}/visit × ${tier.avgVisitsPerCustomerPerMonth ?? 0}/mo`;
  }
  if (model === 'hybrid') {
    return `${tier.name} - $${tier.price}/mo + visits`;
  }
  const period =
    tier.billingFrequency === 'yearly'
      ? 'year'
      : tier.billingFrequency === 'quarterly'
        ? 'quarter'
        : 'month';
  return `${tier.name} - $${tier.price}/${period}`;
}

function formatPlanCurrency(amount: number): string {
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function billingPeriodLabel(
  frequency: SubscriptionTier['billingFrequency'],
): string {
  switch (frequency) {
    case 'yearly':
      return 'year';
    case 'quarterly':
      return 'quarter';
    default:
      return 'month';
  }
}

export interface RevenuePlanCardSummary {
  modelLabel: string;
  priceLine: string;
  detailLine?: string;
}

export function getRevenuePlanCardSummary(
  tier: SubscriptionTier,
): RevenuePlanCardSummary {
  const model = tier.revenueModel ?? 'subscription';
  const period = billingPeriodLabel(tier.billingFrequency);

  if (model === 'per_visit') {
    const pricePerVisit = tier.pricePerVisit ?? 0;
    const visits = tier.avgVisitsPerCustomerPerMonth ?? 0;
    const monthlyPerCustomer = pricePerVisit * visits;
    return {
      modelLabel: 'Per visit',
      priceLine: `$${formatPlanCurrency(pricePerVisit)}/visit`,
      detailLine:
        visits > 0
          ? `~$${formatPlanCurrency(monthlyPerCustomer)}/mo per customer (${visits} visits/mo)`
          : 'Set avg visits per customer on the plan',
    };
  }

  if (model === 'hybrid') {
    const pricePerVisit = tier.pricePerVisit ?? 0;
    const visits = tier.avgVisitsPerCustomerPerMonth ?? 0;
    const visitRevenue = pricePerVisit * visits;
    return {
      modelLabel: 'Hybrid',
      priceLine: `$${formatPlanCurrency(tier.price)}/${period}`,
      detailLine: `+ $${formatPlanCurrency(pricePerVisit)}/visit × ${visits}/mo (~$${formatPlanCurrency(visitRevenue)} visit revenue)`,
    };
  }

  return {
    modelLabel: 'Subscription',
    priceLine: `$${formatPlanCurrency(tier.price)}/${period}`,
  };
}

export function getRevenuePlanLabel(tier: SubscriptionTier): string {
  return formatRevenuePlanLabel(tier);
}

function monthlySubscriptionPrice(tier: SubscriptionTier): number {
  switch (tier.billingFrequency) {
    case 'quarterly':
      return tier.price / 3;
    case 'yearly':
      return tier.price / 12;
    default:
      return tier.price;
  }
}

function monthlyRevenuePerCustomer(tier: SubscriptionTier): number {
  const model = tier.revenueModel ?? 'subscription';
  const visitRevenue =
    (tier.pricePerVisit ?? 0) * (tier.avgVisitsPerCustomerPerMonth ?? 0);

  if (model === 'per_visit') return visitRevenue;
  if (model === 'hybrid') return monthlySubscriptionPrice(tier) + visitRevenue;
  return monthlySubscriptionPrice(tier);
}

function formatShortDate(value: Date | string | undefined): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatPercent(rate: number): string {
  const percent = rate * 100;
  return `${percent % 1 === 0 ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

export interface CustomerProjectionCardSummary {
  typeLabel: string;
  growthLine: string;
  revenuePlanLine: string;
  detailLines: string[];
  revenueHighlight: string;
  revenueHighlightLabel: string;
}

export function getCustomerProjectionCardSummary(
  projection: CustomerProjection,
  tier?: SubscriptionTier,
  capacityGroup?: CustomerCapacityGroup,
): CustomerProjectionCardSummary {
  const initialCustomers = projection.initialCustomers ?? 0;
  const churnPct = formatPercent(projection.churnRateMonthly ?? 0);

  let typeLabel = 'Customer projection';
  let growthLine = '';

  switch (projection.projectionType) {
    case 'fixed_monthly':
      typeLabel = 'Fixed acquisition';
      growthLine = `+${(projection.monthlyAcquisitionTarget ?? 0).toLocaleString()} customers/mo`;
      break;
    case 'growth_rate': {
      typeLabel = 'Growth + acquisition';
      const growthPct = formatPercent(projection.monthlyGrowthRate ?? 0);
      const fixed = projection.monthlyAcquisitionTarget ?? 0;
      growthLine =
        fixed > 0
          ? `${growthPct} monthly growth + ${fixed.toLocaleString()} new/mo`
          : `${growthPct} monthly growth`;
      break;
    }
    case 'custom_timeline': {
      typeLabel = 'Custom timeline';
      const timeline = projection.customTimeline ?? [];
      if (timeline.length > 0) {
        const peak = [...timeline].sort(
          (a, b) => b.expectedCustomers - a.expectedCustomers,
        )[0];
        growthLine = peak
          ? `${peak.expectedCustomers.toLocaleString()} customers by month ${peak.month}`
          : 'Custom customer path';
      } else {
        growthLine = 'Custom customer path (no milestones yet)';
      }
      break;
    }
  }

  const revenuePlanLine = tier
    ? `Revenue plan: ${tier.name}`
    : 'Revenue plan: not linked';

  const detailLines: string[] = [];

  const startLabel = formatShortDate(projection.startDate);
  if (startLabel) detailLines.push(`Starts ${startLabel}`);

  detailLines.push(
  `${initialCustomers.toLocaleString()} starting customers`,
  );
  detailLines.push(`${churnPct} monthly churn`);
  detailLines.push(
    `$${formatPlanCurrency(projection.acquisitionCostPerCustomer)} acquisition cost`,
  );

  if ((projection.monthlyServicingCostPerCustomer ?? 0) > 0) {
    detailLines.push(
      `$${formatPlanCurrency(projection.monthlyServicingCostPerCustomer ?? 0)}/mo servicing per customer`,
    );
  }

  if (projection.maxCustomers) {
    detailLines.push(`Cap ${projection.maxCustomers.toLocaleString()} customers`);
  }

  if (projection.maxMonthlyAcquisitions) {
    detailLines.push(
      `Max ${projection.maxMonthlyAcquisitions.toLocaleString()} new customers/mo`,
    );
  }

  if (projection.capacityGroupId) {
    detailLines.push(
      capacityGroup
        ? `Shared: ${capacityGroup.name} (max ${capacityGroup.maxCustomers.toLocaleString()})`
        : 'Linked to shared capacity group',
    );
  }

  const equilibrium = calculateEquilibriumCustomers({
    projectionType: projection.projectionType,
    subscriptionTierId: projection.subscriptionTierId,
    startDate: projection.startDate,
    churnRateMonthly: projection.churnRateMonthly,
    monthlyAcquisitionTarget: projection.monthlyAcquisitionTarget,
    monthlyGrowthRate: projection.monthlyGrowthRate,
  });
  if (equilibrium !== null) {
    detailLines.push(`~${Math.round(equilibrium).toLocaleString()} steady-state customers`);
  }

  const monthlyRevenue = tier
    ? initialCustomers * monthlyRevenuePerCustomer(tier)
    : 0;

  return {
    typeLabel,
    growthLine,
    revenuePlanLine,
    detailLines,
    revenueHighlight: tier
      ? `$${formatPlanCurrency(monthlyRevenue)}/mo`
      : '—',
    revenueHighlightLabel: initialCustomers > 0 ? 'Starting revenue' : 'Revenue at start',
  };
}

export function createCustomerProjectionFinancialEntries(data: CustomerProjectionData): {
  subscriptionRevenues: Record<string, unknown>[];
  acquisitionCostExpenses: Record<string, unknown>[];
  servicingCostExpenses: Record<string, unknown>[];
} {
  const subscriptionRevenues: Record<string, unknown>[] = [];
  const acquisitionCostExpenses: Record<string, unknown>[] = [];
  const servicingCostExpenses: Record<string, unknown>[] = [];

  data.projections.forEach((projection) => {
    const revenuePlan = data.subscriptionTiers.find(
      (tier) => tier.id === projection.subscriptionTierId,
    );
    if (!revenuePlan) return;

    subscriptionRevenues.push({
      id: `customer-projection-revenue-${projection.id}`,
      name: `Customer Revenue - ${revenuePlan.name}`,
      amount: 0,
      monthlyAmount: 0,
      type: 'subscription',
      category: 'subscription',
      startDate: projection.startDate,
      endDate: projection.endDate,
      isRecurring: true,
      frequency: 'monthly',
      isCustomerDriven: true,
      customerProjectionId: projection.id,
      subscriptionTierId: projection.subscriptionTierId,
      startupId: projection.startupId,
      createdAt: new Date(),
      updatedAt: new Date(),
    });

    if (projection.acquisitionCostPerCustomer && projection.acquisitionCostPerCustomer > 0) {
      acquisitionCostExpenses.push({
        id: `customer-projection-expense-${projection.id}`,
        name: `Customer Acquisition Costs - ${revenuePlan.name}`,
        monthlyAmount: 0,
        category: 'marketing',
        expenseType: 'variable',
        startDate: projection.startDate,
        endDate: projection.endDate,
        isRecurring: true,
        isOneTime: false,
        frequency: 'monthly',
        customerProjectionId: projection.id,
        acquisitionCostPerCustomer: projection.acquisitionCostPerCustomer,
        startupId: projection.startupId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }

    if (
      projection.monthlyServicingCostPerCustomer &&
      projection.monthlyServicingCostPerCustomer > 0
    ) {
      servicingCostExpenses.push({
        id: `customer-projection-servicing-${projection.id}`,
        name: `Customer Servicing - ${revenuePlan.name}`,
        monthlyAmount: 0,
        category: 'operations',
        expenseType: 'variable',
        startDate: projection.startDate,
        endDate: projection.endDate,
        isRecurring: true,
        isOneTime: false,
        frequency: 'monthly',
        customerProjectionId: projection.id,
        startupId: projection.startupId,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
    }
  });

  return { subscriptionRevenues, acquisitionCostExpenses, servicingCostExpenses };
}
